(() => {
  const namespaceKey = 'GuruShopProductsStory';
  const markerSelector = 'script[data-guru-products-story-root]';
  const sectionSelector = '[data-guru-products-story]';
  const existingApi = window[namespaceKey];

  if (existingApi?.scan) {
    existingApi.scan(document);
    return;
  }

  const activeRoots = new Set();
  const initializedRoots = new WeakSet();
  const cleanupByRoot = new WeakMap();

  const owningRoot = (node) => node?.closest?.('[id^="shopify-section-"]') || node;

  function initRoot(root) {
    if (!root || initializedRoots.has(root)) return false;

    const section = root.matches?.(sectionSelector) ? root : root.querySelector?.(sectionSelector);
    if (!section) return false;

    const carousel = section.querySelector('[data-guru-products-carousel]');
    const track = carousel?.querySelector('[data-guru-products-carousel-track]');
    const slides = [...carousel?.querySelectorAll('[data-guru-products-slide]') || []];
    const controls = carousel?.querySelector('[data-guru-products-carousel-controls]');
    const previousButton = controls?.querySelector('[data-guru-products-carousel-previous]');
    const nextButton = controls?.querySelector('[data-guru-products-carousel-next]');
    if (!carousel || !track || slides.length !== 5 || !controls || !previousButton || !nextButton) return false;

    const abortController = new AbortController();
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const motionFrames = [...section.querySelectorAll('[data-guru-products-motion]')];
    let activeIndex = 0;
    let animationFrame = 0;
    let destroyed = false;
    let motionObserver = null;
    let resizeObserver = null;

    const isRtl = () => getComputedStyle(track).direction === 'rtl';

    const setActiveIndex = (nextIndex) => {
      activeIndex = Math.max(0, Math.min(slides.length - 1, nextIndex));
      slides.forEach((slide, index) => {
        const active = index === activeIndex;
        slide.classList.toggle('is-active', active);
        if (active) slide.setAttribute('aria-current', 'true');
        else slide.removeAttribute('aria-current');
      });
      previousButton.disabled = activeIndex === 0;
      nextButton.disabled = activeIndex === slides.length - 1;
    };

    const nearestSlideIndex = () => {
      const trackRect = track.getBoundingClientRect();
      const edge = isRtl() ? trackRect.right : trackRect.left;
      return slides.reduce((nearest, slide, index) => {
        const rect = slide.getBoundingClientRect();
        const distance = Math.abs((isRtl() ? rect.right : rect.left) - edge);
        const nearestRect = slides[nearest].getBoundingClientRect();
        const nearestDistance = Math.abs((isRtl() ? nearestRect.right : nearestRect.left) - edge);
        return distance < nearestDistance ? index : nearest;
      }, 0);
    };

    const updateFromScroll = () => {
      animationFrame = 0;
      if (!destroyed) setActiveIndex(nearestSlideIndex());
    };

    const requestScrollUpdate = () => {
      if (destroyed || animationFrame) return;
      animationFrame = window.requestAnimationFrame(updateFromScroll);
    };

    const scrollToSlide = (nextIndex) => {
      if (destroyed) return;
      const boundedIndex = Math.max(0, Math.min(slides.length - 1, nextIndex));
      setActiveIndex(boundedIndex);
      slides[boundedIndex].scrollIntoView({
        behavior: reducedMotion.matches ? 'auto' : 'smooth',
        block: 'nearest',
        inline: 'start',
      });
    };

    const handleKeydown = (event) => {
      let nextIndex = null;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = slides.length - 1;
      if (event.key === 'ArrowLeft') nextIndex = activeIndex + (isRtl() ? 1 : -1);
      if (event.key === 'ArrowRight') nextIndex = activeIndex + (isRtl() ? -1 : 1);
      if (nextIndex === null) return;
      event.preventDefault();
      scrollToSlide(nextIndex);
    };

    const stopContainedMotion = () => {
      motionObserver?.disconnect();
      motionObserver = null;
      motionFrames.forEach((frame) => frame.classList.remove('is-motion-active'));
    };

    const startContainedMotion = () => {
      stopContainedMotion();
      if (destroyed || reducedMotion.matches || !('IntersectionObserver' in window)) return;
      motionObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!destroyed && !reducedMotion.matches) {
            entry.target.classList.toggle('is-motion-active', entry.isIntersecting && entry.intersectionRatio > 0);
          }
        });
      }, { rootMargin: '10% 0px', threshold: [0, 0.15] });
      motionFrames.forEach((frame) => motionObserver.observe(frame));
    };

    const syncMotionPreference = () => {
      if (reducedMotion.matches) stopContainedMotion();
      else startContainedMotion();
    };

    previousButton.addEventListener('click', () => scrollToSlide(activeIndex - 1), { signal: abortController.signal });
    nextButton.addEventListener('click', () => scrollToSlide(activeIndex + 1), { signal: abortController.signal });
    track.addEventListener('scroll', requestScrollUpdate, { passive: true, signal: abortController.signal });
    track.addEventListener('keydown', handleKeydown, { signal: abortController.signal });
    reducedMotion.addEventListener('change', syncMotionPreference);

    if ('ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(requestScrollUpdate);
      resizeObserver.observe(track);
      slides.forEach((slide) => resizeObserver.observe(slide));
    } else {
      window.addEventListener('resize', requestScrollUpdate, { passive: true, signal: abortController.signal });
    }

    const cleanup = () => {
      if (destroyed) return;
      destroyed = true;
      abortController.abort();
      reducedMotion.removeEventListener('change', syncMotionPreference);
      stopContainedMotion();
      resizeObserver?.disconnect();
      if (animationFrame) window.cancelAnimationFrame(animationFrame);
      animationFrame = 0;
      section.classList.remove('is-enhanced');
      controls.hidden = true;
      slides.forEach((slide) => {
        slide.classList.remove('is-active');
        slide.removeAttribute('aria-current');
      });
      delete root.dataset.guruProductsStoryBound;
      activeRoots.delete(root);
      initializedRoots.delete(root);
      cleanupByRoot.delete(root);
    };

    initializedRoots.add(root);
    cleanupByRoot.set(root, cleanup);
    activeRoots.add(root);
    root.dataset.guruProductsStoryBound = 'true';
    section.classList.add('is-enhanced');
    controls.hidden = false;
    setActiveIndex(0);
    startContainedMotion();
    return true;
  }

  function cleanupRoot(root) {
    const cleanup = cleanupByRoot.get(root);
    if (!cleanup) return false;
    cleanup();
    return true;
  }

  const pruneDisconnectedRoots = () => {
    [...activeRoots].forEach((root) => {
      if (!root.isConnected) cleanupRoot(root);
    });
  };

  function initById(rootId) {
    if (!rootId) return false;
    return initRoot(document.getElementById(rootId));
  }

  function scan(scope = document) {
    pruneDisconnectedRoots();
    const markers = [];
    if (scope.matches?.(markerSelector)) markers.push(scope);
    markers.push(...scope.querySelectorAll?.(markerSelector) || []);
    markers.forEach((marker) => initById(marker.dataset.guruProductsStoryRoot));

    const sections = [];
    if (scope.matches?.(sectionSelector)) sections.push(scope);
    sections.push(...scope.querySelectorAll?.(sectionSelector) || []);
    sections.forEach((section) => initRoot(owningRoot(section)));
  }

  document.addEventListener('shopify:section:load', (event) => {
    initRoot(event.target);
    scan(event.target);
  });
  document.addEventListener('shopify:section:unload', (event) => cleanupRoot(event.target));

  window[namespaceKey] = { cleanupRoot, initById, initRoot, scan };
  scan(document);

  const mutationObserver = new MutationObserver((records) => {
    records.forEach((record) => record.removedNodes.forEach((node) => {
      if (!(node instanceof Element)) return;
      if (activeRoots.has(node)) cleanupRoot(node);
      node.querySelectorAll('[data-guru-products-story-bound]').forEach(cleanupRoot);
    }));
    records.forEach((record) => record.addedNodes.forEach((node) => {
      if (node instanceof Element) scan(node);
    }));
    pruneDisconnectedRoots();
  });
  mutationObserver.observe(document.documentElement, { childList: true, subtree: true });
})();
